import { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from './Card';
import { Button } from './Button';
import { Input } from './Input';
import { useAssetStore, Asset } from '@/stores/assetStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useTranslation } from 'react-i18next';
import { unifiedApiService } from '@/services/unifiedApi';
import { AssetSearchResult, AssetType } from '@/types/api';
import { LoadingIndicator } from './LoadingIndicator';
import { useDebouncedCallback } from '@/utils/debounce';
import { getAssetTypeColor } from '@/utils/assetTypeConfig';
import { useErrorHandler } from '@/hooks/useErrorHandler';

export const AssetManagement = () => {
  const { t } = useTranslation();
  const { showError } = useErrorHandler();
  const { assets, addAsset, removeAsset, removeAssets, updateAssetHoldings, importAssets, exportAssets } = useAssetStore();
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AssetSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<string | null>(null);
  const [showBulkConfirmDialog, setShowBulkConfirmDialog] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addFormRef = useRef<HTMLDivElement>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);
  
  // Request cancellation and sequence tracking
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const searchSequenceRef = useRef(0);

  // Debounced search function (no type restriction - uses smart detection)
  const performSearch = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    // Cancel previous search request
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    // Create new abort controller and increment sequence
    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;
    const currentSequence = ++searchSequenceRef.current;

    setIsSearching(true);
    setValidationError(null);

    try {
      // Pass undefined as type to enable smart detection, and pass abort signal
      const results = await unifiedApiService.searchAssets(query, undefined, {
        signal: abortController.signal
      });
      
      // Only update results if this is still the latest search request
      if (currentSequence === searchSequenceRef.current && !abortController.signal.aborted) {
        setSearchResults(results);
      }
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      
      // Only update error state if this is still the latest search request
      if (currentSequence === searchSequenceRef.current) {
        console.error('Search error:', error);
        setValidationError(error instanceof Error ? error.message : 'Search failed');
        setSearchResults([]);
      }
    } finally {
      // Only update loading state if this is still the latest search request
      if (currentSequence === searchSequenceRef.current) {
        setIsSearching(false);
      }
    }
  }, []);

  // Use debounced callback for search with dynamic delay
  // Longer delay for short queries to reduce unnecessary requests
  // Create two debounced functions at component top level (React Hook rules)
  const debouncedSearchShort = useDebouncedCallback(performSearch, 800);
  const debouncedSearchLong = useDebouncedCallback(performSearch, 500);

  // Handle search input with debouncing
  useEffect(() => {
    if (searchQuery.length >= 2) {
      // Use longer delay for short queries (< 3 chars)
      if (searchQuery.length < 3) {
        debouncedSearchShort(searchQuery);
      } else {
        debouncedSearchLong(searchQuery);
      }
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, debouncedSearchShort, debouncedSearchLong]);

  // Highlight add form when it's shown (no scrolling)
  useEffect(() => {
    if (showAddForm && addFormRef.current) {
      // Highlight the form after a short delay to ensure it's rendered
      setTimeout(() => {
        // Highlight the form
        setIsHighlighted(true);
        // Remove highlight after animation
        setTimeout(() => setIsHighlighted(false), 2000);
      }, 100);
    }
  }, [showAddForm]);

  // Check for duplicate assets
  const isDuplicate = useCallback((symbol: string, type: AssetType): boolean => {
    return assets.some(
      asset => asset.symbol.toLowerCase() === symbol.toLowerCase() && asset.type === type
    );
  }, [assets]);

  const isRateLimitError = useCallback((error: unknown): boolean => {
    if (!error) return false;
    if (typeof error === 'object' && 'code' in error) {
      return (error as any).code === 'RATE_LIMIT_ERROR';
    }
    const message = typeof error === 'string'
      ? error
      : (error as any)?.message || '';
    return (
      message.includes('status code 429') ||
      message.includes('RATE_LIMIT') ||
      message.toLowerCase().includes('rate limit') ||
      /\b429\b/.test(message)
    );
  }, []);

  // Validate and add asset
  const handleAddAsset = async (searchResult: AssetSearchResult) => {
    // Prevent repeated clicks creating concurrent validations
    if (isValidating) return;

    // Check for duplicates
    if (isDuplicate(searchResult.symbol, searchResult.type)) {
      setValidationError(t('assets.assetExists', { symbol: searchResult.symbol, type: searchResult.type }));
      return;
    }

    setIsValidating(true);
    setValidationError(null);

    try {
      // Validate asset through API
      const validation = await unifiedApiService.validateAsset({
        symbol: searchResult.symbol,
        name: searchResult.name,
        type: searchResult.type,
        ...(searchResult.exchange && { exchange: searchResult.exchange })
      });

      if (!validation.valid) {
        // If rate-limited during validation, allow adding the asset and fetch price later
        if (isRateLimitError(validation.error)) {
          const baseAsset: Asset = {
            id: searchResult.id || `${searchResult.type}-${searchResult.symbol.toLowerCase()}`,
            symbol: searchResult.symbol.toUpperCase(),
            name: searchResult.name,
            type: searchResult.type,
            priceStatus: 'loading',
            displayOrder: assets.length,
            isActive: true,
            category: 'Watchlist',
            holdings: 0,
          };
          if (searchResult.exchange) {
            baseAsset.exchange = searchResult.exchange;
          }
          addAsset(baseAsset);
          showError(t('assets.addedButRateLimited', { symbol: searchResult.symbol.toUpperCase() }), {
            code: 'RATE_LIMIT_ERROR',
            context: 'asset-add'
          });
          setValidationError(null);
          setSearchQuery('');
          setSearchResults([]);
          setShowAddForm(false);
          return;
        }
        
        // If asset was found in search results but validation failed (e.g., all providers failed),
        // still allow adding it with loading state - price will be fetched later
        // This handles cases where search works but validation fails due to temporary API issues
        console.warn(`Validation failed for ${searchResult.symbol}, but allowing add since it was found in search:`, validation.error);
        const baseAsset: Asset = {
          id: searchResult.id || `${searchResult.type}-${searchResult.symbol.toLowerCase().replace(/\./g, '_')}`,
          symbol: searchResult.symbol.toUpperCase(),
          name: searchResult.name,
          type: searchResult.type,
          priceStatus: 'loading',
          displayOrder: assets.length,
          isActive: true,
          category: 'Watchlist',
          holdings: 0,
        };
        if (searchResult.exchange) {
          baseAsset.exchange = searchResult.exchange;
        }
        addAsset(baseAsset);
        // Show a warning but don't block the add
        showError(t('assets.addedButValidationFailed', { symbol: searchResult.symbol.toUpperCase() }) || `已添加 ${searchResult.symbol.toUpperCase()}，但验证失败，价格稍后获取`, {
          code: 'VALIDATION_WARNING',
          context: 'asset-add'
        });
        setValidationError(null);
        setSearchQuery('');
        setSearchResults([]);
        setShowAddForm(false);
        return;
      }

      // Use the validated asset with price data if available
      const validatedAsset = validation.asset;
      
      // Build base asset
      const baseAsset: Asset = {
        id: validatedAsset?.id || searchResult.id || `${searchResult.type}-${searchResult.symbol.toLowerCase()}`,
        symbol: searchResult.symbol.toUpperCase(),
        name: searchResult.name,
        type: searchResult.type,
        priceStatus: validatedAsset?.price ? 'success' : 'loading',
        displayOrder: assets.length,
        isActive: true,
        category: 'Watchlist', // Set default category
        holdings: 0, // Set default holdings to 0
      };
      
      // Add optional fields
      if (searchResult.exchange) {
        baseAsset.exchange = searchResult.exchange;
      }
      
      // Add price data if available
      if (validatedAsset?.price !== undefined) {
        baseAsset.price = validatedAsset.price;
      }
      if (validatedAsset?.change24h !== undefined) {
        baseAsset.change24h = validatedAsset.change24h;
      }
      if (validatedAsset?.changePercent24h !== undefined) {
        baseAsset.changePercent24h = validatedAsset.changePercent24h;
      }
      if (validatedAsset?.lastUpdated) {
        baseAsset.lastUpdated = typeof validatedAsset.lastUpdated === 'string' 
          ? validatedAsset.lastUpdated 
          : validatedAsset.lastUpdated.toISOString();
      }
      // Carry source URL from validation result if provider supplied it
      const validatedSourceUrl = (validatedAsset as any)?.sourceUrl;
      if (typeof validatedSourceUrl === 'string' && validatedSourceUrl) {
        baseAsset.sourceUrl = validatedSourceUrl;
      }

      addAsset(baseAsset);
      
      // Reset form
      setSearchQuery('');
      setSearchResults([]);
      setShowAddForm(false);
      setValidationError(null);
    } catch (error) {
      console.error('Validation error:', error);
      // If validation request was rate-limited, allow adding and fetch price later
      if (isRateLimitError(error)) {
        const baseAsset: Asset = {
          id: searchResult.id || `${searchResult.type}-${searchResult.symbol.toLowerCase()}`,
          symbol: searchResult.symbol.toUpperCase(),
          name: searchResult.name,
          type: searchResult.type,
          priceStatus: 'loading',
          displayOrder: assets.length,
          isActive: true,
          category: 'Watchlist',
          holdings: 0,
        };
        if (searchResult.exchange) {
          baseAsset.exchange = searchResult.exchange;
        }
        addAsset(baseAsset);
        showError(t('assets.addedButRateLimited', { symbol: searchResult.symbol.toUpperCase() }), {
          code: 'RATE_LIMIT_ERROR',
          context: 'asset-add'
        });
        setValidationError(null);
        setSearchQuery('');
        setSearchResults([]);
        setShowAddForm(false);
        return;
      }
      setValidationError(error instanceof Error ? error.message : t('assets.validationFailed'));
    } finally {
      setIsValidating(false);
    }
  };

  // Handle asset removal with confirmation
  const handleRemoveAsset = (assetId: string) => {
    setShowConfirmDialog(assetId);
  };

  const confirmRemoveAsset = (assetId: string) => {
    removeAsset(assetId);
    setShowConfirmDialog(null);
  };

  // Bulk operations
  const toggleAssetSelection = (assetId: string) => {
    setSelectedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const selectAllAssets = () => {
    setSelectedAssets(new Set(assets.map(a => a.id)));
  };

  const deselectAllAssets = () => {
    setSelectedAssets(new Set());
  };

  const handleBulkRemove = () => {
    if (selectedAssets.size === 0) return;
    setShowBulkConfirmDialog(true);
  };

  const confirmBulkRemove = () => {
    removeAssets(Array.from(selectedAssets));
    setSelectedAssets(new Set());
    setShowBulkConfirmDialog(false);
    setBulkMode(false);
  };

  // Import/Export functionality
  const handleExport = () => {
    try {
      const assetsToExport = exportAssets();
      const settingsStore = useSettingsStore.getState();
      
      // 收集所有配置数据
      const exportData = {
        version: '1.0',
        assets: assetsToExport,
        settings: {
          // API Keys
          apiKeys: {
            twelvedata: settingsStore.apiKeys.twelvedata,
            coinmarketcap: settingsStore.apiKeys.coinmarketcap,
            alphavantage: settingsStore.apiKeys.alphavantage,
            finnhub: settingsStore.apiKeys.finnhub,
            marketstack: settingsStore.apiKeys.marketstack,
            financialmodelingprep: settingsStore.apiKeys.financialmodelingprep,
          },
          // URLs
          aktoolsBaseUrl: settingsStore.aktoolsBaseUrl,
          // 其他设置
          updateInterval: settingsStore.updateInterval,
          exchangeRates: settingsStore.exchangeRates,
        },
        // 环境变量中的API Keys（用于参考，实际使用settings中的值）
        envApiKeys: {
          VITE_TWELVEDATA_API_KEY: (import.meta as any)?.env?.VITE_TWELVEDATA_API_KEY || null,
          VITE_COINMARKETCAP_API_KEY: (import.meta as any)?.env?.VITE_COINMARKETCAP_API_KEY || null,
          VITE_ALPHAVANTAGE_API_KEY: (import.meta as any)?.env?.VITE_ALPHAVANTAGE_API_KEY || null,
          VITE_FINNHUB_API_KEY: (import.meta as any)?.env?.VITE_FINNHUB_API_KEY || null,
          VITE_MARKETSTACK_API_KEY: (import.meta as any)?.env?.VITE_MARKETSTACK_API_KEY || null,
          VITE_FINANCIALMODELINGPREP_API_KEY: (import.meta as any)?.env?.VITE_FINANCIALMODELINGPREP_API_KEY || null,
        },
      };
      
      if (!assetsToExport || assetsToExport.length === 0) {
        // 即使没有资产，也导出配置
        console.log('Exporting config only (no assets)');
      }
      
      const dataStr = JSON.stringify(exportData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `dashboard-config-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Show success toast for browser download - centered with checkmark icon
        const toast = document.createElement('div');
        toast.className = 'fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 flex items-center gap-3';
        
        // Create checkmark icon with white circle background
        const iconWrapper = document.createElement('div');
        iconWrapper.className = 'flex-shrink-0 w-6 h-6 bg-white rounded-full flex items-center justify-center';
        
        const checkIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        checkIcon.setAttribute('class', 'w-4 h-4 text-green-500');
        checkIcon.setAttribute('fill', 'none');
        checkIcon.setAttribute('viewBox', '0 0 24 24');
        checkIcon.setAttribute('stroke', 'currentColor');
        checkIcon.setAttribute('stroke-width', '3');
        
        const checkPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        checkPath.setAttribute('stroke-linecap', 'round');
        checkPath.setAttribute('stroke-linejoin', 'round');
        checkPath.setAttribute('d', 'M5 13l4 4L19 7');
        
        checkIcon.appendChild(checkPath);
        iconWrapper.appendChild(checkIcon);
        toast.appendChild(iconWrapper);
        
        // Create text element
        const textElement = document.createElement('span');
        textElement.textContent = t('assets.exportedToDownloads') || '已下载到浏览器下载目录';
        toast.appendChild(textElement);
        
        document.body.appendChild(toast);
        setTimeout(() => document.body.removeChild(toast), 3000);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importedData = JSON.parse(content);
        
        // 支持新格式（包含settings）和旧格式（只有assets数组）
        let importedAssets: Asset[] = [];
        let importedSettings: any = null;
        
        if (importedData.version && importedData.assets) {
          // 新格式：包含配置
          importedAssets = importedData.assets;
          importedSettings = importedData.settings;
        } else if (Array.isArray(importedData)) {
          // 旧格式：只有资产数组
          importedAssets = importedData;
        } else {
          setValidationError(t('assets.invalidFileFormat'));
          return;
        }
        
        // Validate imported assets
        if (!Array.isArray(importedAssets)) {
          setValidationError(t('assets.invalidFileFormat'));
          return;
        }

        // Basic validation of asset structure
        const validAssets = importedAssets.filter(asset => 
          asset.id && asset.symbol && asset.name && asset.type
        );

        // 导入资产
        if (validAssets.length > 0) {
          importAssets(validAssets);
        }

        // 导入设置（API Keys 和 URLs）
        if (importedSettings) {
          const settingsStore = useSettingsStore.getState();
          
          // 导入 API Keys
          if (importedSettings.apiKeys) {
            if (importedSettings.apiKeys.twelvedata) {
              settingsStore.setApiKey('twelvedata', importedSettings.apiKeys.twelvedata);
            }
            if (importedSettings.apiKeys.coinmarketcap) {
              settingsStore.setApiKey('coinmarketcap', importedSettings.apiKeys.coinmarketcap);
            }
            if (importedSettings.apiKeys.alphavantage) {
              settingsStore.setApiKey('alphavantage', importedSettings.apiKeys.alphavantage);
            }
            if (importedSettings.apiKeys.finnhub) {
              settingsStore.setApiKey('finnhub', importedSettings.apiKeys.finnhub);
            }
            if (importedSettings.apiKeys.marketstack) {
              settingsStore.setApiKey('marketstack', importedSettings.apiKeys.marketstack);
            }
            if (importedSettings.apiKeys.financialmodelingprep) {
              settingsStore.setApiKey('financialmodelingprep', importedSettings.apiKeys.financialmodelingprep);
            }
          }
          
          // 导入 Aktools Base URL
          if (importedSettings.aktoolsBaseUrl) {
            settingsStore.setAktoolsBaseUrl(importedSettings.aktoolsBaseUrl);
          }
          
          // 导入其他设置
          if (importedSettings.updateInterval) {
            settingsStore.setUpdateInterval(importedSettings.updateInterval);
          }
          if (importedSettings.exchangeRates) {
            if (importedSettings.exchangeRates.USD_HKD) {
              settingsStore.setExchangeRate('USD_HKD', importedSettings.exchangeRates.USD_HKD);
            }
            if (importedSettings.exchangeRates.USD_CNY) {
              settingsStore.setExchangeRate('USD_CNY', importedSettings.exchangeRates.USD_CNY);
            }
          }
        }

        setValidationError(null);
        
        // Show success message
        const assetCount = validAssets.length;
        const settingsCount = importedSettings ? 
          (Object.values(importedSettings.apiKeys || {}).filter((v: any) => v).length + 
           (importedSettings.aktoolsBaseUrl ? 1 : 0)) : 0;
        const message = assetCount > 0 && settingsCount > 0
          ? `成功导入 ${assetCount} 个自选和 ${settingsCount} 项配置`
          : assetCount > 0
          ? t('assets.importSuccess', { count: assetCount })
          : settingsCount > 0
          ? `成功导入 ${settingsCount} 项配置`
          : t('assets.noValidAssetsFound');
        alert(message);
      } catch (error) {
        console.error('Import error:', error);
        setValidationError(t('assets.importError'));
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Use assets directly without filtering
  const filteredAssets = assets;

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('assets.title')}
          </h3>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={handleExport} 
              disabled={assets.length === 0}
            >
              {t('assets.export')} {assets.length > 0 && `(${assets.length})`}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()}>
              {t('assets.import')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
            <Button 
              size="sm" 
              variant={bulkMode ? 'secondary' : 'ghost'}
              onClick={() => {
                setBulkMode(!bulkMode);
                setSelectedAssets(new Set());
              }}
              disabled={assets.length === 0}
            >
              {bulkMode ? t('assets.cancelBulk') : t('assets.bulkEdit')}
            </Button>
            <Button 
              size="sm" 
              onClick={() => {
                const willShow = !showAddForm;
                setShowAddForm(willShow);
                if (willShow) {
                  // Highlight the form after a short delay to ensure it's rendered
                  setTimeout(() => {
                    // Highlight the form
                    setIsHighlighted(true);
                    // Remove highlight after animation
                    setTimeout(() => setIsHighlighted(false), 2000);
                  }, 100);
                }
              }}
            >
              {t('assets.add')}
            </Button>
          </div>
        </div>

        {/* Bulk Mode Actions */}
        {bulkMode && (
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {selectedAssets.size} {t('assets.selected')}
                </span>
                <Button size="sm" variant="ghost" onClick={selectAllAssets}>
                  {t('assets.selectAll')}
                </Button>
                <Button size="sm" variant="ghost" onClick={deselectAllAssets} disabled={selectedAssets.size === 0}>
                  {t('assets.deselectAll')}
                </Button>
              </div>
              <Button 
                size="sm" 
                onClick={handleBulkRemove}
                disabled={selectedAssets.size === 0}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {t('assets.removeSelected')} ({selectedAssets.size})
              </Button>
            </div>
          </div>
        )}

        {showAddForm && (
          <div 
            ref={addFormRef}
            className={`space-y-3 p-3 rounded-lg transition-all duration-500 ${
              isHighlighted 
                ? 'bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-500 dark:border-blue-400 shadow-lg' 
                : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
            }`}
          >
            {/* Search Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('assets.searchAsset')}
              </label>
              <Input
                placeholder={t('assets.searchPlaceholder.all')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={isValidating}
              />
              {isSearching && (
                <div className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <LoadingIndicator size="sm" />
                  <span>{t('assets.searching')}</span>
                </div>
              )}
            </div>

            {/* Validation Error */}
            {validationError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{validationError}</p>
              </div>
            )}

            {/* Search Results */}
            {searchResults.length > 0 && !isSearching && (
              <div className="max-h-64 overflow-y-auto space-y-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('assets.searchResults')} ({searchResults.length})
                </p>
                {searchResults.map((result) => {
                  const duplicate = isDuplicate(result.symbol, result.type);
                  return (
                    <div
                      key={`${result.type}-${result.symbol}`}
                      className={`p-3 rounded-lg border ${
                        duplicate
                          ? 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 opacity-60'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {result.symbol}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getAssetTypeColor(result.type)}`}>
                              {result.type.charAt(0).toUpperCase() + result.type.slice(1)}
                            </span>
                            {duplicate && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300">
                                {t('assets.alreadyAdded')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {result.name}
                          </p>
                          {result.exchange && (
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                              {t('assets.exchange')}: {result.exchange}
                            </p>
                          )}
                          {result.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              {result.description}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAddAsset(result)}
                          disabled={isValidating || duplicate}
                        >
                          {isValidating ? t('assets.validating') : t('assets.add')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* No Results Message */}
            {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && !validationError && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('assets.noResults', { query: searchQuery })}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAddForm(false);
                  setSearchQuery('');
                  setSearchResults([]);
                  setValidationError(null);
                }}
              >
                {t('assets.cancel')}
              </Button>
            </div>
          </div>
        )}

        {/* Asset List */}
        <div className="space-y-2">
          {filteredAssets.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <p className="text-sm">
                {assets.length === 0 
                  ? t('assets.noAssetsYet')
                  : t('assets.noMatchingAssets')}
              </p>
            </div>
          ) : (
            filteredAssets.map((asset) => (
              <div
                key={asset.id}
                className={`flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg ${
                  bulkMode ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''
                }`}
                onClick={() => bulkMode && toggleAssetSelection(asset.id)}
              >
                {bulkMode && (
                  <input
                    type="checkbox"
                    checked={selectedAssets.has(asset.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleAssetSelection(asset.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {asset.name}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getAssetTypeColor(asset.type)}`}>
                      {asset.type.charAt(0).toUpperCase() + asset.type.slice(1)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {asset.symbol}
                    {asset.exchange && !asset.symbol.toUpperCase().endsWith(`.${asset.exchange.toUpperCase()}`) && ` • ${asset.exchange}`}
                  </div>
                </div>
                {!bulkMode && (
                  <div className="flex gap-2 items-center">
                    {asset.type !== 'index' && asset.type !== 'forex' && (
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={asset.holdings || 0}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            updateAssetHoldings(asset.id, value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.target.select()}
                          min="0"
                          step="0.01"
                          className="w-24 px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="0.0"
                        />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {asset.symbol}
                        </span>
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAsset(asset.id)}
                    >
                      {t('assets.remove')}
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('assets.confirmRemoval')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('assets.confirmRemovalMessage')}
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowConfirmDialog(null)}
                >
                  {t('assets.cancel')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => confirmRemoveAsset(showConfirmDialog)}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {t('assets.remove')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Confirmation Dialog */}
        {showBulkConfirmDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('assets.confirmBulkRemoval')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('assets.confirmBulkRemovalMessage', { count: selectedAssets.size })}
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowBulkConfirmDialog(false)}
                >
                  {t('assets.cancel')}
                </Button>
                <Button
                  size="sm"
                  onClick={confirmBulkRemove}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {t('assets.remove')} {selectedAssets.size} {t('portfolio.assets')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

